import {isRight} from 'fp-ts/lib/Either'
import React, {
  createContext,
  ReactElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'
import {variableResolverFactory} from '../../common/card-assembler'
import {getResolvedExpression} from '../../common/card-assembler/resolvers'
import {
  VariableCodec,
  VariablesRecord
} from '../../common/types/variable-engine'
import {useAssets} from '../../hooks/use-assets'
import {Updator} from '../../views/card-builder/editor'
import {
  UseActiveVariable,
  useActiveVariableInternal
} from './use-active-variable'
import {VariableModalContext} from './use-variable-modal'
export {useVariableModal} from './use-variable-modal'

type ValueResolver = (expression: string) => Promise<string>

const VariablePaths = createContext<string[]>([])

const VariableResolver = createContext<ValueResolver>(s => Promise.resolve(s))

const Variables = createContext<VariablesRecord>({})

export function useVariablePaths() {
  return useContext(VariablePaths)
}

export function useVariableResolver() {
  return useContext(VariableResolver)
}

export function useVariables() {
  return useContext(Variables)
}

const VariableEditContext = createContext<UseActiveVariable | undefined>(
  undefined
)

export function useActiveVariable() {
  const editContext = useContext(VariableEditContext)
  if (!editContext) {
    throw new Error('useActiveVariable must be called within VariableProvider')
  }

  return editContext
}

type VariableProviderProps = {
  organisationId: string
  environmentId: string
  variables: VariablesRecord
  children: ReactElement
  providerId?: string
}

const validVariables = (variables: VariablesRecord): VariablesRecord => {
  return Object.entries(variables).reduce<VariablesRecord>(
    (valid, [name, variable]) => {
      if (isRight(VariableCodec.decode(variable))) {
        return {...valid, [name]: variable}
      }
      return valid
    },
    {}
  )
}

const getVariablePaths = (variables: VariablesRecord): string[] => {
  const names = Object.keys(validVariables(variables))
  return names.sort()
}

export function VariableProvider({
  organisationId,
  environmentId,
  variables,
  children,
  providerId
}: VariableProviderProps) {
  const getAssetUrl = useAssets(organisationId, environmentId)
  const [variablePaths, setVariablesPath] = useState<string[]>(() =>
    getVariablePaths(variables)
  )

  const variableEdit = useActiveVariableInternal(variables, providerId)

  useEffect(() => {
    const next = getVariablePaths(variables)
    setVariablesPath(vars => {
      // shallow compare here to prevent variablePaths triggering use effects when it shouldn't
      if (vars.toString() !== next.toString()) {
        return next
      }
      return vars
    })
  }, [variables])

  const variablesForProvider = useMemo(
    () => validVariables(variables),
    [variables]
  )

  const variableResolver = useMemo(
    () => variableResolverFactory(variables, {}, {}, getAssetUrl, true),
    [variables, getAssetUrl]
  )

  const resolveValue = useCallback(
    (expression: string) =>
      getResolvedExpression(expression, variableResolver, {
        timezone: 'Pacific/Auckland'
      }),
    [variableResolver]
  )

  return (
    <Variables.Provider value={variablesForProvider}>
      <VariablePaths.Provider value={variablePaths}>
        <VariableResolver.Provider value={resolveValue}>
          <VariableEditContext.Provider value={variableEdit}>
            {children}
          </VariableEditContext.Provider>
        </VariableResolver.Provider>
      </VariablePaths.Provider>
    </Variables.Provider>
  )
}

type LegacyVariableProviderProps = VariableProviderProps & {
  updateVariables: (
    variables: VariablesRecord | Updator<VariablesRecord>,
    rename?: {from: string; to: string}
  ) => void
}

// Used by the legacy card builder, this should be removed once
// we have transitioned to the AF card builder

export function LegacyVariableProvider({
  organisationId,
  environmentId,
  variables,
  updateVariables,
  children,
  providerId
}: LegacyVariableProviderProps) {
  const modalContext = useMemo(
    () => ({
      variables,
      updateVariables,
      providerId
    }),
    [variables, updateVariables, providerId]
  )

  return (
    <VariableProvider
      organisationId={organisationId}
      environmentId={environmentId}
      variables={variables}
      providerId={providerId}
    >
      <VariableModalContext.Provider value={modalContext}>
        {children}
      </VariableModalContext.Provider>
    </VariableProvider>
  )
}
